import React from 'react';
import {
  DocsPage,
  HeroSection,
  FeatureGrid,
  FeatureCard,
  InfoBox,
  StepsList,
  CalloutCard,
  CTAButton,
  NetworkBadge,
} from '../../components/docs/DocsComponents';

const Introduction: React.FC = () => {
  return (
    <DocsPage>
      <HeroSection>
        <h1>Welcome to Mavrk.ink</h1>
        <p className="subtitle">
          A comprehensive DeFi ecosystem built on the Ink Layer 2 chain that revolutionizes token deployment, trading, and cross-chain operations. Unlike traditional platforms that require significant capital upfront, Mavrk enables completely free token launches with gasless deployments and instant liquidity provision.
        </p>
      </HeroSection>

      <h2>Revolutionary DeFi Platform</h2>
      <p>
        <strong>Mavrk.ink</strong> is a comprehensive DeFi ecosystem built on the <strong>Ink Layer 2</strong> chain that revolutionizes token deployment, trading, and cross-chain operations. Unlike traditional platforms that require significant capital upfront, Mavrk enables <strong>completely free token launches</strong> with <strong>gasless deployments</strong> and <strong>instant liquidity provision</strong>.
      </p>

      <h2>Core Features</h2>

      <h3>Completely Free Token Creation</h3>
      <ul>
        <li><strong>Gasless deployments</strong> sponsored by Gelato ERC-2771</li>
        <li><strong>Wallet-less deployment</strong> option with temporary wallet generation</li>
        <li><strong>Zero upfront capital</strong> required</li>
        <li><strong>Instant liquidity</strong> using 100% token supply</li>
      </ul>

      <h3>Ultra-Low Cost Operations</h3>
      <ul>
        <li>Built on Ink Layer 2 for <strong>fraction-of-a-cent transactions</strong></li>
        <li><strong>Gasless token creation</strong> via Gelato sponsorship</li>
        <li><strong>Self-funded option</strong> available for advanced users</li>
        <li><strong>Cross-chain bridging</strong> with minimal fees</li>
      </ul>

      <h3>25% Creator Earnings System</h3>
      <ul>
        <li><strong>25% of all LP trading fees</strong> go directly to token creators</li>
        <li><strong>75% to Mavrk</strong> for platform sustainability</li>
        <li><strong>Automatic fee collection</strong> and distribution</li>
        <li><strong>Real-time earnings tracking</strong> in your profile</li>
      </ul>

      <h3>Advanced Bridge & Swap</h3>
      <ul>
        <li><strong>Multi-chain bridging</strong> (Ethereum, Base, Polygon, Arbitrum, Optimism, Avalanche, BSC)</li>
        <li><strong>Cross-chain token swaps</strong> powered by Relay Protocol</li>
        <li><strong>Real-time balance tracking</strong> across all supported chains</li>
        <li><strong>Percentage-based amount selection</strong> (25%, 50%, 75%, Max)</li>
      </ul>

      <h3>Comprehensive Wallet System</h3>
      <ul>
        <li><strong>Metamask-style wallet interface</strong> with token management</li>
        <li><strong>Token locking functionality</strong> with customizable durations</li>
        <li><strong>Send/Receive capabilities</strong> for all supported tokens</li>
        <li><strong>User-managed token holdings</strong> with add/remove functionality</li>
        <li><strong>Real-time USD value tracking</strong> for all tokens</li>
      </ul>

      <h3>Modern User Experience</h3>
      <ul>
        <li><strong>Sleek, responsive design</strong> with dark theme</li>
        <li><strong>Mobile-optimized interface</strong> with touch-friendly controls</li>
        <li><strong>Real-time price feeds</strong> via Relay Protocol API (5x faster)</li>
        <li><strong>Comprehensive token discovery</strong> with search and filtering</li>
        <li><strong>Social features</strong> and community favorites</li>
      </ul>

      <h2>How It Works</h2>

      <h3>Token Creation Process</h3>
      <StepsList>
        <li>
          <strong>Configure Token:</strong> Set name, symbol, description, logo, and social links for your token.
        </li>
        <li>
          <strong>Choose Deployment:</strong> Select gasless (free) or self-funded deployment option based on your needs.
        </li>
        <li>
          <strong>Deploy:</strong> Factory contract creates optimized ERC20 token with ERC-2771 support for gasless transactions.
        </li>
        <li>
          <strong>Liquidity:</strong> 100% token supply automatically added to Uniswap V3 concentrated liquidity pool for optimal trading.
        </li>
        <li>
          <strong>Lock:</strong> LP NFT transferred to MavrkLocker for permanent security and fee distribution to creators.
        </li>
        <li>
          <strong>Trade:</strong> Immediate trading availability with real-time pricing and ultra-low fees on Ink L2.
        </li>
      </StepsList>

      <h3>Bridge & Swap Process</h3>
      <StepsList>
        <li>
          <strong>Select Chains:</strong> Choose source and destination chains from 7+ supported networks.
        </li>
        <li>
          <strong>Enter Amount:</strong> Use percentage buttons (25%, 50%, 75%, Max) or manual input for precise amounts.
        </li>
        <li>
          <strong>Bridge:</strong> Relay Protocol securely handles cross-chain transfer with real-time updates.
        </li>
        <li>
          <strong>Monitor:</strong> Track progress via transaction links with automatic network switching.
        </li>
      </StepsList>

      <h3>Wallet Management</h3>
      <StepsList>
        <li>
          <strong>Connect Wallet:</strong> Link your Web3 wallet or use wallet-less option
        </li>
        <li>
          <strong>Manage Tokens:</strong> Add/remove tokens from your holdings
        </li>
        <li>
          <strong>Lock Tokens:</strong> Set custom lock durations for any ERC-20 token
        </li>
        <li>
          <strong>Send/Receive:</strong> Transfer tokens with QR code support
        </li>
      </StepsList>

      <h2>Built on Ink Layer 2</h2>
      
      <ul>
        <li><strong>Chain ID:</strong> 57073</li>
        <li><strong>Explorer:</strong> <a href="https://explorer.inkonchain.com" target="_blank" rel="noopener noreferrer">explorer.inkonchain.com</a></li>
        <li><strong>Native Token:</strong> ETH (WETH: <code>0x4200000000000000000000000000000000000006</code>)</li>
        <li><strong>RPC:</strong> Multiple failover providers for reliability</li>
      </ul>

      <h2>Get Started</h2>

      <p>Ready to experience the future of DeFi? Follow our <a href="/docs/quick-start">Quick Start Guide</a> to get up and running in minutes!</p>

      <h3>What You Can Do Today</h3>
      <ul>
        <li><strong>Create tokens for free</strong> with gasless deployment</li>
        <li><strong>Bridge assets</strong> across 7+ major chains</li>
        <li><strong>Trade tokens</strong> with real-time pricing</li>
        <li><strong>Lock tokens</strong> for custom durations</li>
        <li><strong>Earn 25%</strong> of all trading fees on your tokens</li>
        <li><strong>Manage your portfolio</strong> with our advanced wallet</li>
      </ul>

      <InfoBox variant="warning">
        <div className="info-title">Note</div>
        <p>
          This platform is for educational and experimental purposes only. Token creation and trading involve significant risks. Always do your own research before investing.
        </p>
      </InfoBox>

    </DocsPage>
  );
};

export default Introduction;
